import {
  ORG_LIFECYCLE_GRACE_SUMMARY_SUFFIX,
  ORG_LIFECYCLE_MUTATION_HINT_NO_DETAIL,
  ORG_LIFECYCLE_MUTATION_HINT_STATUS_ONLY,
  ORG_LIFECYCLE_NORMAL_MODE_SUMMARY_LINE,
  ORG_LIFECYCLE_SUMMARY_NEEDS_PAYMENT,
  ORG_LIFECYCLE_SUMMARY_POST_GRACE
} from './org-lifecycle-copy';
import type { OrgPlanStatus, OrganizationPlanChoiceItem, PlanPackageComposition } from './tariff-org-types';

type OrgPlanChoice = OrganizationPlanChoiceItem;

/** Подписи к module_code (каталог platform_modules / план) — для витрины, не marketing. */
const MODULE_CODE_LABEL: Record<string, string> = {
  booking: 'Букинг',
  fitness: 'Фитнес',
  equipment: 'Оборудование',
  subscriptions: 'Абонементы',
  schedule: 'Расписание',
  payments: 'Платежи (модуль)',
  automation: 'Автоматизация (модуль)',
  sales_funnel: 'Воронка продаж (модуль)',
  yclients: 'YClients',
  admin_rbac: 'Роли и доступ',
  qrcode: 'QR-доступ'
};

/** Человекочитаемое имя кода модуля — для toast, карточек, единой политики UX (см. admin-module-ux). */
export function moduleCodeLabelRu(code: string): string {
  if (!code) return code;
  return MODULE_CODE_LABEL[code] ?? code;
}

export function formatModuleCodesForDisplay(codes: string[] | undefined): string {
  if (!codes || codes.length === 0) return '—';
  return codes.map((c) => MODULE_CODE_LABEL[c] ?? c).join(', ');
}

function includedCodesSet(plan: OrgPlanChoice | undefined): Set<string> {
  const raw = plan?.package_composition?.included_module_codes;
  if (!Array.isArray(raw)) return new Set();
  return new Set(raw.filter((c): c is string => typeof c === 'string' && c.length > 0));
}

/**
 * Коды модулей, которые встречаются в **других** планах каталога, но **не** в текущем пакете.
 * Не гарантирует «после смены появится у org» (это назначение плана + organization_modules) — только сравнение витрин.
 */
export function computeCatalogUpgradeModuleCodes(
  currentPlan: OrgPlanChoice | undefined,
  allPlans: OrgPlanChoice[]
): string[] {
  if (currentPlan == null || allPlans.length < 2) return [];
  const have = includedCodesSet(currentPlan);
  const unionOther = new Set<string>();
  for (const p of allPlans) {
    if (p.id === currentPlan.id) continue;
    for (const c of includedCodesSet(p)) {
      unionOther.add(c);
    }
  }
  const out: string[] = [];
  for (const c of unionOther) {
    if (!have.has(c)) out.push(c);
  }
  out.sort();
  return out;
}

/** ADR-0019: staff read-only по org-мутациям в льготе / пост-льготе (см. org_tariff_write, баннер). */
export function isPlanStatusLifecycleBlockingActions(status: OrgPlanStatus): boolean {
  return status.in_org_tariff_grace === true || status.post_grace_unpaid === true;
}

/**
 * Сообщение, почему кнопки смены тарифа могут быть неуместны/заблокированы (без дублирования длинного текста баннера).
 */
export function buildTariffMutationHint(
  status: OrgPlanStatus,
  orgTariffWrite: { allowed: boolean; detail?: string | null } | null | undefined
): string | null {
  if (orgTariffWrite && orgTariffWrite.allowed === false) {
    const d = (orgTariffWrite.detail ?? '').trim();
    return d || ORG_LIFECYCLE_MUTATION_HINT_NO_DETAIL;
  }
  if (isPlanStatusLifecycleBlockingActions(status)) {
    return ORG_LIFECYCLE_MUTATION_HINT_STATUS_ONLY;
  }
  return null;
}

/** Даты льгот в user-facing баннерах (shell, «Мой тариф»). */
export function formatOrgPlanGraceDateRu(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso.includes('T') ? iso : `${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatGraceDate(iso: string | null | undefined): string {
  return formatOrgPlanGraceDateRu(iso);
}

/**
 * Краткое резюме «что в пакете плана».
 * Если в каталоге есть `package_composition` для текущего plan_id — используем его (честный compose из plan_modules + флаги).
 * Иначе — фолбэк по флагам plan-status (ось договора на org).
 */
export function buildPlanPackSummaryText(
  status: OrgPlanStatus,
  catalogRowForCurrentPlan: OrgPlanChoice | null | undefined
): string {
  const pc = catalogRowForCurrentPlan?.package_composition as PlanPackageComposition | undefined;
  if (pc && Array.isArray(pc.included_module_codes) && pc.plan_flags) {
    const mods = formatModuleCodesForDisplay(pc.included_module_codes);
    const pf = pc.plan_flags;
    const auto = pf.automation_enabled !== false;
    const sf = pf.sales_funnel_enabled !== false;
    const emb = pf.embed_brand_single_org === true;
    return (
      `Состав пакета в каталоге тарифов: модули — ${mods}. ` +
      `Флаги плана: автоматизация — ${auto ? 'да' : 'нет'}, воронка — ${sf ? 'да' : 'нет'}, бренд-embed — ${
        emb ? 'да' : 'нет'
      }. ` +
      `Это витрина подписки (plan_modules + колонки плана), не снимок organization_modules org.`
    );
  }
  const items: Array<{ on: boolean; label: string }> = [
    { on: status.automation_enabled !== false, label: 'автоматизация' },
    { on: status.sales_funnel_enabled !== false, label: 'воронка продаж' },
    { on: status.embed_brand_single_org === true, label: 'бренд-встраивание (embed)' }
  ];
  const on = items.filter((x) => x.on).map((x) => x.label);
  const off = items.filter((x) => !x.on).map((x) => x.label);
  if (on.length === items.length) {
    return `По договору плана в тариф входят: ${on.join(', ')}.`;
  }
  if (on.length === 0) {
    return `По договору плана в тариф сейчас не входят: ${off.join(', ')}.`;
  }
  return `По договору плана в тариф входят: ${on.join(', ')}. Отдельно не входят: ${off.join(', ')}.`;
}

/**
 * Один абзац про lifecycle, если требуется (баннер в shell подробнее).
 */
export function buildLifecycleSummaryLine(status: OrgPlanStatus): string | null {
  if (status.post_grace_unpaid === true) {
    return ORG_LIFECYCLE_SUMMARY_POST_GRACE;
  }
  if (status.in_org_tariff_grace === true && (status.org_tariff_grace_ends_at ?? '').length > 0) {
    return `Льготный период после срока оплаты: до ${formatGraceDate(status.org_tariff_grace_ends_at)} — ${ORG_LIFECYCLE_GRACE_SUMMARY_SUFFIX}`;
  }
  if (status.needs_payment_banner === true) {
    return ORG_LIFECYCLE_SUMMARY_NEEDS_PAYMENT;
  }
  return null;
}

/**
 * Когда plan-status не в grace/post/needs_payment — одна нейтральная строка «нормальный тарифный режим».
 */
export function buildNormalSubscriptionStatusLine(status: OrgPlanStatus): string | null {
  if (status.has_plan !== true) return null;
  if (status.post_grace_unpaid === true) return null;
  if (status.needs_payment_banner === true) return null;
  if (status.in_org_tariff_grace === true) return null;
  return ORG_LIFECYCLE_NORMAL_MODE_SUMMARY_LINE;
}

export function buildLimitsSummaryText(status: OrgPlanStatus): string {
  if (status.over_limit_employees || status.over_limit_clients || status.over_limit_branches) {
    const p: string[] = [];
    if (status.over_limit_employees) p.push('сотрудники');
    if (status.over_limit_clients) p.push('клиенты');
    if (status.over_limit_branches) p.push('филиалы');
    return `Превышен лимит по: ${p.join(', ')} — смена тарифа или сокращение нагрузки.`;
  }
  if (status.near_limit_employees || status.near_limit_clients || status.near_limit_branches) {
    return 'Лимит по сотрудникам, клиентам или филиалам близок к 95% — планируйте смену тарифа.';
  }
  return 'Лимиты сотрудников, клиентов и филиалов в рамках текущего плана.';
}

/**
 * Квотный limit grace (отдельно от org-tariff grace / post_grace). Только на «Тариф организации»;
 * в shell — баннер; здесь — компактная ось смысла (soft, не hard 403).
 */
export function buildQuotaLimitAxisSummaryText(status: OrgPlanStatus): string | null {
  const hasOver =
    status.over_limit_employees === true || status.over_limit_clients === true || status.over_limit_branches === true;
  const until = (status.limit_grace_until ?? '').trim();

  if (status.in_tariff_limit_grace === true && until.length > 0) {
    return (
      `Лимиты почти исчерпаны: контрольная дата — ${formatGraceDate(status.limit_grace_until)}. ` +
      `Это предупреждение: жёсткой блокировки нет, услуги продолжают работать.`
    );
  }
  if (hasOver && status.in_tariff_limit_grace !== true && until.length > 0) {
    return (
      `Лимиты превышены: контрольная дата прошла (${formatGraceDate(status.limit_grace_until)}). ` +
      `Режим «только просмотр» включается только при сценарии срока подписки вверху, а не из-за квот.`
    );
  }
  return null;
}

/**
 * Строки для секции «функции плана» (две колонки: да/нет).
 * Если передан `catalogRow` с `package_composition.plan_flags` — берём флаги оттуда (синхрон с каталогом).
 */
export function getPlanPackFeatureRows(status: OrgPlanStatus, catalogRowForCurrentPlan: OrgPlanChoice | null | undefined) {
  const pf = catalogRowForCurrentPlan?.package_composition?.plan_flags;
  const auto = pf ? pf.automation_enabled !== false : status.automation_enabled !== false;
  const funnel = pf ? pf.sales_funnel_enabled !== false : status.sales_funnel_enabled !== false;
  const embed = pf ? pf.embed_brand_single_org === true : status.embed_brand_single_org === true;
  return [
    {
      id: 'automation' as const,
      name: 'Автоматизация',
      hint: 'Сценарии, источники, входящие заявки — в объёме, заложенном в плане (колонка каталога).',
      included: auto
    },
    {
      id: 'funnel' as const,
      name: 'Воронка продаж',
      hint: 'Сценарии продаж, если они входят в тариф. Не путать с модулями вроде «Фитнес».',
      included: funnel
    },
    {
      id: 'embed' as const,
      name: 'Бренд и встраивание (embed)',
      hint: 'Отдельно от остального CRM; не путать с модулями вроде «Фитнес».',
      included: embed
    }
  ];
}

const PAYMENTS_MODULE_CODE = 'payments';

/** Состояние строки «пакет каталога vs effective-модуль org» (без payments — см. отдельную ось). */
export type PlanOrgModuleDiffState =
  | 'in_plan_and_enabled'
  | 'in_plan_not_enabled'
  /** Включено у org по capabilities, но слота нет в текущем пакете (legacy / другой путь / смена каталога). */
  | 'enabled_not_in_package';

export type PlanOrgModuleDiffRow = {
  code: string;
  label: string;
  state: PlanOrgModuleDiffState;
  inPackage: boolean;
  orgModuleOn: boolean;
};

/**
 * Честный diff по module_code: план (`package_composition.included_module_codes`) vs `modules_effective` из /auth/capabilities.
 * Код `payments` не включается — ось приёма оплат задаётся отдельно (модуль + `organizations.payments_enabled`).
 */
export function buildPlanOrgModuleDiffRows(
  includedModuleCodes: string[] | undefined,
  modulesEffective: Record<string, boolean> | undefined
): PlanOrgModuleDiffRow[] {
  const eff = modulesEffective ?? {};
  const included = new Set(
    (includedModuleCodes ?? []).filter((c) => typeof c === 'string' && c.length > 0 && c !== PAYMENTS_MODULE_CODE)
  );
  const codes = new Set<string>(included);
  for (const k of Object.keys(eff)) {
    if (k === PAYMENTS_MODULE_CODE) continue;
    if (eff[k] === true) codes.add(k);
  }
  const sorted = [...codes].sort();
  const out: PlanOrgModuleDiffRow[] = [];
  for (const code of sorted) {
    const inPackage = included.has(code);
    const orgModuleOn = eff[code] === true;
    let state: PlanOrgModuleDiffState;
    if (inPackage && orgModuleOn) state = 'in_plan_and_enabled';
    else if (inPackage && !orgModuleOn) state = 'in_plan_not_enabled';
    else state = 'enabled_not_in_package';
    out.push({
      code,
      label: MODULE_CODE_LABEL[code] ?? code,
      state,
      inPackage,
      orgModuleOn
    });
  }
  return out;
}

export type PaymentsAxisRow = {
  /** Слот `payments` в plan_modules / витрине пакета. */
  paymentsInPackage: boolean;
  /** Effective org-модуль `payments` (capabilities). */
  modulePaymentsEffective: boolean;
  /** Колонка org: приём/учёт платежей (GET /organizations/me → `payments_enabled`). */
  orgPaymentsEnabled: boolean | null;
};

/**
 * Ось приёма оплат: не смешивать слот каталога, модуль и org-флаг.
 * `orgPaymentsEnabled === null` — снимок org не загружен (не рисуем «да/нет» по колонке org).
 */
export function buildPaymentsAxisRow(
  includedModuleCodes: string[] | undefined,
  modulesEffective: Record<string, boolean> | undefined,
  orgPaymentsEnabled: boolean | null
): PaymentsAxisRow {
  const inc = new Set((includedModuleCodes ?? []).filter((c) => typeof c === 'string' && c.length > 0));
  const eff = modulesEffective ?? {};
  return {
    paymentsInPackage: inc.has(PAYMENTS_MODULE_CODE),
    modulePaymentsEffective: eff[PAYMENTS_MODULE_CODE] === true,
    orgPaymentsEnabled
  };
}

export function planOrgDiffStateLabel(state: PlanOrgModuleDiffState): string {
  switch (state) {
    case 'in_plan_and_enabled':
      return 'В пакете и включено у org';
    case 'in_plan_not_enabled':
      return 'В пакете, у org ещё не включено';
    case 'enabled_not_in_package':
      return 'Включено у org, в текущем пакете нет слота';
    default:
      return state;
  }
}
